import React, { useEffect } from "react";
import { graphql } from "react-relay";
import { useLazyLoadQuery, usePaginationFragment } from "react-relay/hooks";

import { useConnectionNodes } from "components/common/Pagination";
import { useSelector } from "hooks/redux";
import { getOrgUuid, isUserInInternalOrg } from "state/current_user/selectors";
import { formatUserName } from "utils/common";
import {
  GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_EMAIL,
  GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_ID,
  GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_UUID,
} from "utils/constants";
import { MIN_DATE_FILTER } from "utils/date";

import SelectFilter from "./SelectFilter";

import type { FC } from "react";
import type { PilotFilter_organization$key } from "__generated__/PilotFilter_organization.graphql";
import type { PilotFilterQuery } from "__generated__/PilotFilterQuery.graphql";
import type { User } from "utils/common";
import type { BaseProps } from "./SelectFilter";

export const userFragment = graphql`
  fragment PilotFilter_organization on Organization
  @argumentDefinitions(
    cursor: { type: "String" }
    count: { type: "Int", defaultValue: 10 }
    hasFlightAfterDate: { type: "DateTime" }
  )
  @refetchable(queryName: "PilotFilterRefetchQuery") {
    id
    users(first: $count, after: $cursor, hasFlightAfterDate: $hasFlightAfterDate)
      @connection(key: "PilotFilter_users") {
      edges {
        node {
          id
          uuid
          displayName
          email
        }
      }
    }
  }
`;

export const query = graphql`
  query PilotFilterQuery(
    $orgUuid: String!
    $cursor: String
    $count: Int!
    $hasFlightAfterDate: DateTime
  ) {
    organization(uuid: $orgUuid) {
      ...PilotFilter_organization
        @arguments(cursor: $cursor, count: $count, hasFlightAfterDate: $hasFlightAfterDate)
    }
  }
`;

interface Props extends BaseProps {
  onSelect?: (userId: string) => void;
  getValue?: (pilot: User) => string;
  onlyShowPilots?: boolean;
  showAutonomousPilot?: boolean;
}

const PAGE_SIZE = 100;

const PilotFilter: FC<Props> = ({
  onSelect,
  getValue,
  className,
  style,
  placeholder,
  paramKey,
  onlyShowPilots,
  showAutonomousPilot,
}) => {
  const orgUuid = useSelector(getOrgUuid);
  const isInInternalOrg = useSelector(isUserInInternalOrg);
  const data = useLazyLoadQuery<PilotFilterQuery>(query, {
    orgUuid,
    count: PAGE_SIZE,
    hasFlightAfterDate: onlyShowPilots ? MIN_DATE_FILTER : undefined,
  });

  const paginationQuery = usePaginationFragment<PilotFilterQuery, PilotFilter_organization$key>(
    userFragment,
    data.organization
  );

  useEffect(() => {
    if (paginationQuery.hasNext) {
      paginationQuery.loadNext(PAGE_SIZE);
    }
  }, [paginationQuery]);

  const paginatedUsers = paginationQuery?.data?.users;

  // stub for unknown vehicle pilot states (e.g Beacon Flight, Scheduled Flight, Pilot Undetermined);
  // will appear at the top of the select filter options
  const genericVehicleUserStub = {
    node: {
      id: GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_ID,
      uuid: GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_UUID,
      displayName: GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_EMAIL,
      email: GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_EMAIL,
    },
  };

  const users =
    showAutonomousPilot && isInInternalOrg // only expose automatic pilot option in internal orgs due to possibility that above constants may become invalid in the future; possibly remove once a more dynamic solution is implemented
      ? paginatedUsers &&
        paginatedUsers.edges && {
          edges: [genericVehicleUserStub, ...paginatedUsers.edges].filter(edge => edge != null),
        }
      : paginatedUsers;

  return (
    <SelectFilter<User, string>
      onSelect={onSelect}
      className={className}
      style={style}
      placeholder={placeholder || "Search Pilots"}
      data={useConnectionNodes(users)}
      getKey={pilot => pilot?.id!}
      getValue={pilot => (getValue ? getValue(pilot) : pilot?.uuid!)}
      getTitle={pilot => formatUserName(pilot)}
      renderOptionContent={pilot =>
        pilot.email === GENERIC_SKYDIO_UNKNOWN_VEHICLE_USER_EMAIL ? (
          <span className="italic text-gray-400">Autonomous Pilot</span>
        ) : (
          formatUserName(pilot)
        )
      }
      paramKey={paramKey}
    />
  );
};

export default PilotFilter;
